import { useLazyQuery, useMutation } from "@apollo/client";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Input } from "reactstrap";
import { getVideoUploadURLQuery } from "../../../graphql/queries/getVideoUploadURL";
import FormError from "./FormError";
import useVideoUpload from "../../../hooks/useVideoUpload";
import toast from "react-hot-toast";
import { executeDeleteVideo } from "../../../graphql/queries/getImageUploadURL";

interface Props {
    onChange : (id : File) => void;
}

interface CloudFile {
    id : string;
    url : string;
    error : boolean;
}
 
const ImageUploadInput =  forwardRef(function VideoFormInput({ onChange }: Props
    ,ref: React.Ref<{ clearField: () => void, uploadSelectedFile : () => Promise<string>, deleteUploadedFile : () => Promise<void> }>
) {

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const fileIdRef = useRef<CloudFile>({
        id : "", url : "", error : false
    });
    const [file, setFile] = useState<File>(null);

    const [
        getSignedVideoUrl,
        { loading: gettingSignedVideoUrl, error: signedVideoUrlError },
    ] = useLazyQuery(getVideoUploadURLQuery, {
        fetchPolicy: "network-only",
        onCompleted(data) {
            if (data.getVideoUploadURL) {
                fileIdRef.current = {
                    id : data.getVideoUploadURL.cloudflareId,
                    url : data.getVideoUploadURL.uploadURL,
                    error : false
                }
                onChange(data.getVideoUploadURL.cloudflareId);
            }
        },
        onError() {
            fileIdRef.current.error = false;
            console.log('error on getting signed url');
        },
    });

    const { uploadVideo } = useVideoUpload();
    const [deleteVideo] = useMutation(executeDeleteVideo);
    

    useImperativeHandle(ref, () => ({
        clearField: HandleClearField,
        uploadSelectedFile : HandleUploadFile,
        deleteUploadedFile : DeleteVideo
    }));

    const HandleClearField = () => {
        fileIdRef.current = {
            id : "", url : "", error : false
        }
    }

    const GetFileUrl : () => Promise<string> = async () => {
        return new Promise<string>((resolve, reject) => {
            getSignedVideoUrl({
                variables: {
                    fileName: file.name,
                    fileSize: file.size,
                },
            });
            const interval = setInterval(()=>{
                if(fileIdRef.current.error) {
                    reject(null)
                }
                if(fileIdRef.current.id){
                    clearInterval(interval);
                    resolve(fileIdRef.current.id);
                }
            },100);
        })
    }

    
    const HandleOnChange = (e : React.ChangeEvent<HTMLInputElement>) => {
        const file = e.currentTarget.files[0];
        if(file) {
            setFile(file);
            onChange(file);
        }
        else {
            setFile(null);
        
            onChange(null);
        }
    }

    const HandleUploadFile : () => Promise<string> = async () => {

        return new Promise(async (resolve, reject) => {
            if(!file) { return resolve(null); }
            await GetFileUrl();
            return await uploadVideo(fileIdRef.current.url, file, {
                onError: () => {
                  toast.error("Something went wrong whilst uploading your Video. Please try again or if issue persists, please contact us");
                  reject(null);

                },
                onSuccess: () => {
                  resolve(fileIdRef.current.id);
                },
              });
        })
    }

    const DeleteVideo = async () => {
        if(!file){
            return; 
        }
        try{
            await deleteVideo({
                variables : {
                    fileId : fileIdRef.current.id
                },
            })
        }
        catch(err){
            console.log(err);
        }
    }

 
    return (
        <>
            <Input 
                innerRef={fileInputRef}
                type="file" 
                className="form-control"
                onChange={HandleOnChange}
                accept="video/*"
                disabled={gettingSignedVideoUrl}
            />
            {
                signedVideoUrlError && 
                <FormError>Error on setting up for video upload</FormError>
            }
        </>
    )

});

export default ImageUploadInput